/**
 * Fetches OncoEMR tasks from the backend list endpoint.
 * @module fetchOncoemrTasks
 */

import {
  FetchOncoemrTasksByOrderParams,
  FetchOncoemrTasksParams,
  OncoemrTaskListItem,
} from "../../data-model/oncoemrTasksModel";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { getBearerToken } from "../postCall/bearerToken";
import { unwrapBackendListData } from "../unwrapBackendListData";

/**
 * Builds the query string for `GET /oncoemr-tasks/by-order/{order_id}`.
 *
 * @param params - Org scope and optional limit.
 * @returns Encoded query string without leading `?`.
 */
export const buildOncoemrTasksByOrderQueryString = (
  params: FetchOncoemrTasksByOrderParams,
): string => {
  const searchParams = new URLSearchParams({
    org_id: params.org_id,
  });

  if (params.limit !== undefined) {
    searchParams.set("limit", String(params.limit));
  }

  return searchParams.toString();
};

/**
 * Builds the query string for `GET /oncoemr-tasks` (org-wide inbox).
 *
 * `limit` is only serialized when present so callers omitting it do not send `limit=undefined`.
 *
 * @param params - Org scope and optional assignee, since, and limit filters.
 * @returns Encoded query string without leading `?`.
 */
export const buildOncoemrTasksQueryString = (
  params: FetchOncoemrTasksParams,
): string => {
  const searchParams = new URLSearchParams({
    org_id: params.org_id,
  });

  if (params.user_id) {
    searchParams.set("user_id", params.user_id);
  }
  if (params.since) {
    searchParams.set("since", params.since);
  }
  if (params.limit !== undefined) {
    searchParams.set("limit", String(params.limit));
  }

  return searchParams.toString();
};

/**
 * Lists OncoEMR tasks for an organization (optionally scoped to the current user).
 *
 * @param params - Query parameters for org, assignee, since, and limit.
 * @param signal - Optional abort signal for in-flight cancellation.
 * @returns Task list items ordered by `last_activity_at` descending.
 * @throws Error when the HTTP response is not OK or the body is not a list envelope.
 */
export const fetchOncoemrTasks = async (
  params: FetchOncoemrTasksParams,
  signal?: AbortSignal,
): Promise<OncoemrTaskListItem[]> => {
  const query = buildOncoemrTasksQueryString(params);
  const url = `${API_ENDPOINTS.ONCOEMR_TASKS}?${query}`;
  const bearerToken = await getBearerToken();

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${bearerToken}`,
    },
    ...(signal && { signal }),
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch OncoEMR tasks (${response.status} ${response.statusText})`,
    );
  }

  const body: unknown = await response.json();
  return unwrapBackendListData<OncoemrTaskListItem>(
    body,
    response.status,
    "OncoEMR tasks",
  );
};

/**
 * Lists OncoEMR tasks linked to a single order.
 *
 * @param orderId - Medical order id (`order_id` in Postgres).
 * @param params - Query parameters for org scope and optional limit.
 * @param signal - Optional abort signal for in-flight cancellation.
 * @returns Task list items for the order, ordered by `last_activity_at` descending.
 * @throws Error when the HTTP response is not OK or the body is not a list envelope.
 */
export const fetchOncoemrTasksByOrder = async (
  orderId: string,
  params: FetchOncoemrTasksByOrderParams,
  signal?: AbortSignal,
): Promise<OncoemrTaskListItem[]> => {
  const query = buildOncoemrTasksByOrderQueryString({
    ...params,
    limit: params.limit,
  });
  const url = `${API_ENDPOINTS.ONCOEMR_TASKS}/by-order/${encodeURIComponent(orderId)}?${query}`;
  const bearerToken = await getBearerToken();

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${bearerToken}`,
    },
    ...(signal && { signal }),
  });

  if (!response.ok) {
    throw new Error(
      `Failed to fetch OncoEMR tasks for order (${response.status} ${response.statusText})`,
    );
  }

  const body: unknown = await response.json();
  return unwrapBackendListData<OncoemrTaskListItem>(
    body,
    response.status,
    "OncoEMR tasks by-order",
  );
};
